import React, { useState, useEffect } from 'react';
import { TopToolbar, useDataProvider, useNotify, Loading } from 'react-admin';
import { Box, Select, MenuItem, FormControl, InputLabel, Button, Stack } from '@mui/material';
import { Add as AddIcon, TrendingUp as OpportunityIcon } from '@mui/icons-material';
import { OpportunityBoardContent } from './OpportunityBoardContent';
import { CreateOpportunityDialog } from '../../../components/CreateOpportunityDialog';
import { CustomEmptyState } from '../../../components/CustomEmptyState';
import { PageTitle } from '../../../components/PageTitle';
import { useTenant } from '../../../contexts/TenantContext';

const ListActions = () => <TopToolbar>{/* Hide export button as requested */}</TopToolbar>;

interface Board {
  id: string;
  name: string;
  description?: string;
}

export const OpportunityBoard = () => {
  const dataProvider = useDataProvider();
  const notify = useNotify();
  const { currentTenant } = useTenant();
  const [boards, setBoards] = useState<Board[]>([]);
  const [selectedBoardId, setSelectedBoardId] = useState<string>('');
  const [loadingBoards, setLoadingBoards] = useState(true);
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [opportunities, setOpportunities] = useState([]);
  const [loadingOpportunities, setLoadingOpportunities] = useState(false);

  // Load boards on mount
  useEffect(() => {
    const loadBoards = async () => {
      try {
        const { data } = await dataProvider.getList('boards', {
          pagination: { page: 1, perPage: 1000 },
          sort: { field: 'name', order: 'ASC' },
          filter: {},
        });
        setBoards(data);

        // Get last selected board from localStorage or use first board
        const lastSelectedBoardId = localStorage.getItem('lastSelectedBoardId');
        if (lastSelectedBoardId && data.some((board: Board) => board.id === lastSelectedBoardId)) {
          setSelectedBoardId(lastSelectedBoardId);
        } else if (data.length > 0) {
          setSelectedBoardId(data[0].id);
        }
      } catch (error) {
        notify('Error loading boards', { type: 'error' });
      } finally {
        setLoadingBoards(false);
      }
    };

    loadBoards();
  }, [dataProvider, notify, currentTenant?.code]); // Reload boards when tenant changes

  // Function to load opportunities
  const loadOpportunities = async () => {
    if (!selectedBoardId) {
      setOpportunities([]);
      return;
    }

    setLoadingOpportunities(true);
    try {
      const { data } = await dataProvider.getList('opportunities', {
        pagination: { page: 1, perPage: 1000 },
        sort: { field: 'sortOrder', order: 'ASC' },
        filter: { boardId: selectedBoardId },
      });
      setOpportunities(data);
    } catch (error) {
      notify('Error loading opportunities', { type: 'error' });
    } finally {
      setLoadingOpportunities(false);
    }
  };

  // Load opportunities when board changes
  useEffect(() => {
    loadOpportunities();
  }, [selectedBoardId, dataProvider, notify, currentTenant?.code]); // Reload opportunities when tenant or board changes

  const handleBoardChange = (boardId: string) => {
    setSelectedBoardId(boardId);
    localStorage.setItem('lastSelectedBoardId', boardId);
  };

  if (loadingBoards) {
    return <Loading />;
  }

  const selectedBoard = boards.find(board => board.id === selectedBoardId);

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%', position: 'relative', backgroundColor: '#f5f5f5' }}>
      <PageTitle
        title="Opportunities"
        titleKey="pages.opportunities"
        subtitle={selectedBoard ? selectedBoard.name : "Manage your opportunities"}
        icon={<OpportunityIcon />}
        variant="compact"
      />

      <Stack direction="row" alignItems="center" spacing={2} sx={{ mb: 2, px: 3 }}>
        {boards.length > 0 && (
          <FormControl size="small" sx={{ minWidth: 200 }}>
            <InputLabel>Board</InputLabel>
            <Select
              value={selectedBoardId}
              onChange={e => handleBoardChange(e.target.value)}
              label="Board"
            >
              {boards.map(board => (
                <MenuItem key={board.id} value={board.id}>
                  {board.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        )}
        <Button
          variant="outlined"
          startIcon={<AddIcon />}
          onClick={() => setCreateDialogOpen(true)}
        >
          Create Opportunity
        </Button>
      </Stack>

      {/* Direct Board Content - No React-Admin List wrapper */}
      {loadingOpportunities ? (
        <Box sx={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <Loading />
        </Box>
      ) : (
        <Box
          sx={{
            flex: 1,
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden',
            backgroundColor: '#f5f5f5',
            borderRadius: 1,
            position: 'relative',
            minHeight: 0,
          }}
        >
          <OpportunityBoardContent boardId={selectedBoardId} opportunities={opportunities} />
        </Box>
      )}

      {/* Create Opportunity Dialog */}
      <CreateOpportunityDialog
        open={createDialogOpen}
        onClose={() => {
          setCreateDialogOpen(false);
          // Refresh opportunities after dialog closes
          loadOpportunities();
        }}
        defaultBoardId={selectedBoardId}
        showCustomerSelector={true}
      />
    </Box>
  );
};
